import { LDate } from '@app/components/date/LDate';
import { useTimezone } from '@app/core/hooks/useTimezone';
import { toDateTimeStr } from '@mi-front/core';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  DateRangePreset,
  DateRangePresetId,
  DateRangeValue,
  isLastNHoursPreset,
} from './date-range-presets';
import {
  dateRangeLabelToNow,
  DateRangePresetMessageId,
  dateRangePresetMessages,
} from './date-range-selector-messages';
import { formatCustomRangeLabel } from './format-custom-range-label';

export interface DateRangeTriggerLabelProps {
  /** Current range (used to format custom range labels). */
  value?: DateRangeValue | null;
  /** Current preset (determines which label to show). */
  preset?: DateRangePreset | null;
  /** Preset used when preset is not provided. */
  defaultPreset?: DateRangePreset;
}

const DEFAULT_PRESET: DateRangePreset = 'today';

const dateFormatProps = {
  month: 'short' as const,
  day: 'numeric' as const,
  year: 'numeric' as const,
};

type DateRangeStringPresetMessageId = Exclude<DateRangePresetMessageId, 'lastNHours'>;

function hasMessageLabelPreset(
  presetId: DateRangePresetId,
): presetId is DateRangeStringPresetMessageId {
  return presetId !== 'customRange';
}

/**
 * Renders the date range selector trigger content: calendar icon plus
 * a label derived from the current preset or custom range (e.g. "Hoy",
 * "14 de febrero de 2025 - 14 de febrero de 2025").
 * Formats dates in the station timezone so labels match station calendar days.
 */
export function DateRangeTriggerLabel({
  value,
  preset,
  defaultPreset = DEFAULT_PRESET,
}: DateRangeTriggerLabelProps) {
  const { $t } = useIntl();
  const tz = useTimezone();
  const effectivePreset = preset ?? defaultPreset;
  const presetId: DateRangePresetId | null =
    typeof effectivePreset === 'string' ? effectivePreset : null;
  const customRangeLabel = formatCustomRangeLabel(
    value,
    (id) => $t(dateRangePresetMessages[id]),
    tz,
  );

  const label =
    presetId === 'customSingleDate' && value?.from ? (
      <LDate value={toDateTimeStr(value.from)} {...dateFormatProps} tz={tz} />
    ) : presetId === 'fromCustomDateUntilNow' && value?.from ? (
      <FormattedMessage
        {...dateRangeLabelToNow}
        values={{
          from: <LDate value={toDateTimeStr(value.from)} {...dateFormatProps} tz={tz} />,
        }}
      />
    ) : presetId === 'customFixedDateRange' && value?.from && value?.to ? (
      <>
        <LDate value={toDateTimeStr(value.from)} {...dateFormatProps} tz={tz} />
        {' - '}
        <LDate value={toDateTimeStr(value.to)} {...dateFormatProps} tz={tz} />
      </>
    ) : presetId === 'customRange' && customRangeLabel ? (
      customRangeLabel
    ) : isLastNHoursPreset(effectivePreset) ? (
      $t(dateRangePresetMessages.lastNHours, { hours: effectivePreset.hours })
    ) : presetId && hasMessageLabelPreset(presetId) ? (
      $t(dateRangePresetMessages[presetId])
    ) : (
      $t(dateRangePresetMessages.today)
    );

  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
      <CalendarMonthIcon fontSize="small" />
      {label}
    </Box>
  );
}
